import React, { useState, useEffect } from 'react';
import { Table, Spin, Tag, Descriptions, Button, Form, Space, Input } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import axios from 'axios';
import SweetAlert from 'sweetalert2';
import { useAuthHeader, useAuthUser } from '../../../../Services/Auth/sessionAuth';
import Utilisateur from '../../../../Models/Utilisateur';
import Data from '../../../../Data/Data.json';
import { Crypter } from '../../../../Functions/Security/Crypter';
import { Box, Typography } from '@mui/material';
import SearchModal from '../../../SearchModal';
import Params from '../../../../Types/ParamsPicklist';
import formatNumber from '../../../../Functions/Traitement/FormatNumber';

const ValidationReechelonnement = () => {
    const auth = useAuthUser();
    const user: Utilisateur = JSON.parse(JSON.stringify(auth()));
    const authHeader = useAuthHeader();

    const [form] = Form.useForm();
    const [loading, setLoading] = useState(false);
    const [demandes, setDemandes] = useState<any[]>([]);
    const [selectedKey, setSelectedKey] = useState<React.Key | null>(null);
    const [detail, setDetail] = useState<any>(null);

    let searchMotifRej: Params = { CodeAgence: '', Table: "v_motif_reech_rejet", Colonne1: "CODMOTIF", Colonne2: "LIBMOTIF", Colagence: '101', Choix: 22 };

    useEffect(() => { loadListe(); }, []);

    function callApi(action: string, extra: any = {}, onSuccess?: (res: any) => void, silent = false) {
        setLoading(true);
        axios.request({
            method: 'post',
            maxBodyLength: Infinity,
            url: Data.ApiUrl.ReschedulePret,
            headers: { 'Authorization': authHeader(), 'Content-Type': 'application/json' },
            data: Crypter.encrypt(JSON.stringify({
                action,
                numDos: extra.numDos ?? '',
                utiVal: user.coduti,
                codAge: user.codage,
                ...extra
            }))
        })
            .then((response) => {
                setLoading(false);
                if (!response.data) return;
                const res = JSON.parse(Crypter.decrypt(response.data)).Value;
                if (res.StatutCode == 200) {
                    if (onSuccess) onSuccess(res);
                    else if (!silent) SweetAlert.fire({ title: res.Message, icon: "success" });
                } else if (!silent) {
                    SweetAlert.fire({ title: res.Message, icon: "warning" });
                }
            })
            .catch((error) => {
                setLoading(false);
                SweetAlert.fire({ title: String(error), icon: "error" });
            });
    }

    function loadListe() {
        callApi('LISTE', {}, (res) => {
            setDemandes(res.Result?.Table ?? []);
            setDetail(null);
            setSelectedKey(null);
        }, true);
    }

    function loadDetail(id: any, numdos: string) {
        callApi('DETAIL', { idReech: id, numDos: numdos }, (res) => {
            setDetail({
                demande: res.Result?.Table?.[0] ?? null,
                echeances: res.Result?.Table1 ?? []
            });
        }, true);
    }

    const cols: ColumnsType<any> = [
        { title: 'ID', dataIndex: 'Id' },
        { title: 'Dossier', dataIndex: 'NUMDOS' },
        { title: 'Client', dataIndex: 'NOMCLI' },
        { title: 'Type', dataIndex: 'TYPEPRET' },
        { title: 'Ancien capital', dataIndex: 'mntOrig', render: (v: number) => formatNumber(v) },
        { title: 'Nouv. capital', dataIndex: 'MNTCAPIT', render: (v: number) => formatNumber(v) },
        { title: 'Échéances', dataIndex: 'NBRECHE' },
        { title: 'Périod.', dataIndex: 'PERIODIC' },
        { title: '1ère éch.', dataIndex: 'DAT1ECHE', render: (v: string) => v ? String(v).substring(0, 10) : '' },
        { title: 'Frais', dataIndex: 'MTNFRAIS', render: (v: number) => formatNumber(v) },
        { title: 'Motif', dataIndex: 'MOTIF', ellipsis: true },
        { title: 'Par', dataIndex: 'UTILIS' },
        { title: 'Le', dataIndex: 'DATCRE', render: (v: string) => v ? String(v).substring(0, 16).replace('T', ' ') : '' },
        { title: 'État', dataIndex: 'CDETAT', render: (v: string) => <Tag color="gold">{v}</Tag> },
    ];

    const colsEch: ColumnsType<any> = [
        { title: 'N°', dataIndex: 'numSeq' },
        { title: 'Date', dataIndex: 'dateEcheacnce' },
        { title: 'Capital', dataIndex: 'capital', render: (v: number) => formatNumber(v) },
        { title: 'Amort.', dataIndex: 'amortissement', render: (v: number) => formatNumber(v) },
        { title: 'Intérêt', dataIndex: 'interet', render: (v: number) => formatNumber(v) },
        { title: 'Taxe', dataIndex: 'taxe', render: (v: number) => formatNumber(v) },
        { title: 'Annuité TTC', dataIndex: 'anTTC', render: (v: number) => formatNumber(v) },
    ];

    return (
        <div className='tab'>
            <Typography align='center' className='formTitle'><b>Validation des rééchelonnements</b></Typography>
            <Spin spinning={loading} size='large' style={{ color: '#116599' }}>
                <Form form={form} layout="vertical" labelAlign='left'>
                    <div className='bG'>
                        <legend><Typography color={"#116599"}>Actions</Typography></legend>
                        <Form.Item shouldUpdate className='bouttong'>
                            <Space direction="vertical" style={{ width: '100%' }}>
                                <Button size='large' className='bt_add' disabled={!selectedKey} onClick={() => {
                                    const d = demandes.find(x => x.Id === selectedKey);
                                    SweetAlert.fire({
                                        title: `Valider le rééchelonnement du dossier ${d?.NUMDOS} ?`,
                                        text: "L'ancien échéancier impayé sera clôturé et remplacé par le nouveau calendrier.",
                                        showCancelButton: true, confirmButtonText: "Valider", cancelButtonText: "Non",
                                        confirmButtonColor: "#116599", icon: 'question'
                                    }).then((r) => {
                                        if (r.isConfirmed)
                                            callApi('VALIDER', { idReech: selectedKey, numDos: d?.NUMDOS },
                                                (res) => { SweetAlert.fire({ title: res.Message, icon: "success" }); loadListe(); });
                                    });
                                }} type="ghost" htmlType="button" block>Valider</Button>
                                <Button size='large' className='bt_cancel' style={{ color: '#fff' }} disabled={!selectedKey} onClick={() => {
                                    const d = demandes.find(x => x.Id === selectedKey);
                                    if (!form.getFieldValue('MOTIFREJ')) {
                                        SweetAlert.fire({ title: "Sélectionnez un motif de rejet", icon: "warning" }); return;
                                    }
                                    SweetAlert.fire({
                                        title: `Rejeter la demande du dossier ${d?.NUMDOS} ?`,
                                        text: `Motif : ${form.getFieldValue('MOTIFREJ')}${form.getFieldValue('COMREJET') ? ' — ' + form.getFieldValue('COMREJET') : ''}`,
                                        showCancelButton: true, confirmButtonText: "Rejeter", cancelButtonText: "Non",
                                        confirmButtonColor: "#a41b1b", icon: 'warning'
                                    }).then((r) => {
                                        if (r.isConfirmed)
                                            callApi('REJETER', {
                                                idReech: selectedKey, numDos: d?.NUMDOS,
                                                motifRej: form.getFieldValue('MOTIFREJ'),
                                                coment: form.getFieldValue('COMREJET')
                                            },
                                                (res) => { SweetAlert.fire({ title: res.Message, icon: "success" }); loadListe(); });
                                    });
                                }} type="ghost" htmlType="button" block>Rejeter</Button>
                                <Button size='large' className='bt_add' onClick={loadListe} type="ghost" htmlType="button" block>Actualiser</Button>
                            </Space>
                        </Form.Item>
                    </div>
                    <div className='clientP'>
                        <div style={{ width: '88%' }}>
                            <div className='tabItem' style={{ display: 'block', width: '100%' }}>
                                <fieldset className="bordure1" style={{ marginBottom: 12, width: '100%' }}>
                                    <legend style={{ fontSize: '15px', padding: '0 10px', color: '#116599' }}>Demandes en attente</legend>
                                    {demandes.length === 0 ? (
                                        <Typography variant="caption" color="textSecondary">Aucune demande de rééchelonnement en attente.</Typography>
                                    ) : (
                                        <Table size='small' columns={cols} dataSource={demandes}
                                            scroll={{ x: 1100, y: 200 }} pagination={false} rowKey="Id"
                                            rowSelection={{
                                                type: 'radio',
                                                selectedRowKeys: selectedKey ? [selectedKey] : [],
                                                onChange: (keys, rows) => {
                                                    setSelectedKey(keys.length ? keys[0] : null);
                                                    if (keys.length) loadDetail(keys[0], rows[0]?.NUMDOS);
                                                }
                                            }} />
                                    )}
                                </fieldset>

                                {detail?.demande && (
                                    <fieldset className="bordure1" style={{ width: '100%' }}>
                                        <legend style={{ fontSize: '15px', padding: '0 10px', color: '#116599' }}>Demande {detail.demande.Id} — dossier {detail.demande.NUMDOS}</legend>
                                        <Descriptions size="small" column={3} bordered style={{ marginBottom: 8 }}>
                                            <Descriptions.Item label="Client">{detail.demande.NOMCLI}</Descriptions.Item>
                                            <Descriptions.Item label="Capital actuel">{formatNumber(detail.demande.mntOrig)}</Descriptions.Item>
                                            <Descriptions.Item label="Nouveau capital">{formatNumber(detail.demande.MNTCAPIT)}</Descriptions.Item>
                                            <Descriptions.Item label="Impayés capitalisés">{formatNumber(detail.demande.MTNIMPAYE)}</Descriptions.Item>
                                            <Descriptions.Item label="Intérêts cap.">{formatNumber(detail.demande.INTIMPAYE)}</Descriptions.Item>
                                            <Descriptions.Item label="Pénalités cap.">{formatNumber(detail.demande.PENIMPAYE)}</Descriptions.Item>
                                            <Descriptions.Item label="Frais">{formatNumber(detail.demande.MTNFRAIS)}</Descriptions.Item>
                                            <Descriptions.Item label="Code opération">{detail.demande.CODOPE}</Descriptions.Item>
                                            <Descriptions.Item label="Motif">{detail.demande.MOTIF}</Descriptions.Item>
                                        </Descriptions>
                                        <Table size='small' columns={colsEch} dataSource={detail.echeances}
                                            scroll={{ x: 700, y: 200 }} pagination={false} rowKey="numSeq" />
                                        <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0, 1fr))', gap: 2, mt: 2 }}>
                                            <SearchModal width={9} form={form} Titre='Motif du rejet' label='Motif du rejet' name='MOTIFREJ' Parametre={searchMotifRej} />
                                            <Form.Item name="COMREJET" label="Commentaire du rejet">
                                                <Input />
                                            </Form.Item>
                                        </Box>
                                    </fieldset>
                                )}
                            </div>
                        </div>
                    </div>
                </Form>
            </Spin>
        </div>
    );
};

export default ValidationReechelonnement;
